import { useRef, useState } from "react";
import { UploadCloud, File as FileIcon, X } from "lucide-react";
import { Field, TextInput, SelectInput, TextArea, RadioPills, ChipMulti } from "./fields";

export const fmtSSN = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 9);
  return d.length > 4 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d;
};

export const fmtEIN = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 9);
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
};

export const BusinessSection = ({ ctx }) => (
  <div className="space-y-7">
    <Field label="Business legal name" required error={ctx.errors.business_legal_name}>
      <TextInput data-testid="app-biz-name" invalid={!!ctx.errors.business_legal_name}
        value={ctx.form.business_legal_name} onChange={(e) => ctx.set("business_legal_name", e.target.value)}
        placeholder="Acme Robotics LLC" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Legal entity" required error={ctx.errors.legal_entity}>
        <SelectInput data-testid="app-entity" invalid={!!ctx.errors.legal_entity}
          value={ctx.form.legal_entity} onChange={(e) => ctx.set("legal_entity", e.target.value)}
          options={["LLC", "Sole Proprietorship", "Corporation", "S-Corporation", "Partnership", "Non-Profit"]}
          placeholder="Select entity" />
      </Field>
      <Field label="Federal Tax ID (EIN)" required error={ctx.errors.ein}>
        <TextInput data-testid="app-ein" invalid={!!ctx.errors.ein} inputMode="numeric"
          value={ctx.form.ein} onChange={(e) => ctx.set("ein", fmtEIN(e.target.value))}
          placeholder="12-3456789" />
      </Field>
    </div>
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Business phone" required error={ctx.errors.business_phone}>
        <TextInput data-testid="app-biz-phone" type="tel" invalid={!!ctx.errors.business_phone}
          value={ctx.form.business_phone} onChange={(e) => ctx.set("business_phone", e.target.value)}
          placeholder="(555) 000-1234" />
      </Field>
      <Field label="Website (optional)">
        <TextInput data-testid="app-website" value={ctx.form.website} onChange={(e) => ctx.set("website", e.target.value)}
          placeholder="acmerobotics.com" />
      </Field>
    </div>
    <Field label="Business address" required error={ctx.errors.business_address}>
      <TextInput data-testid="app-biz-address" invalid={!!ctx.errors.business_address}
        value={ctx.form.business_address} onChange={(e) => ctx.set("business_address", e.target.value)}
        placeholder="1400 Innovation Dr, Suite 12, Austin TX 78701" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Start date" required error={ctx.errors.start_date}>
        <TextInput data-testid="app-start-date" type="date" invalid={!!ctx.errors.start_date}
          value={ctx.form.start_date} onChange={(e) => ctx.set("start_date", e.target.value)} />
      </Field>
      <Field label="Rent or own?">
        <RadioPills testid="app-rent" value={ctx.form.rent_or_own} onChange={(v) => ctx.set("rent_or_own", v)}
          options={[{ value: "Own", label: "Own" }, { value: "Rent", label: "Rent" }, { value: "Mortgage", label: "Mortgage/Owned" }]} />
      </Field>
    </div>
    <Field label="Industry" required error={ctx.errors.industry}>
      <SelectInput data-testid="app-industry" invalid={!!ctx.errors.industry}
        value={ctx.form.industry} onChange={(e) => ctx.set("industry", e.target.value)}
        options={["Construction", "Retail", "Restaurant / Hospitality", "Healthcare", "Manufacturing", "Transportation / Logistics", "Professional Services", "Technology / SaaS", "Auto / Repair", "Wholesale", "E-commerce", "Real Estate", "Beauty / Salon", "Firearms / gun dealers", "Other"]}
        placeholder="Select industry" />
    </Field>
    <Field label="Any open balances?">
      <RadioPills testid="app-balances" value={ctx.form.open_balances} onChange={(v) => ctx.set("open_balances", v)}
        options={[{ value: "No", label: "No" }, { value: "Yes", label: "Yes" }]} />
    </Field>
    <Field label="Business description" required error={ctx.errors.business_description}>
      <TextArea data-testid="app-description" invalid={!!ctx.errors.business_description}
        value={ctx.form.business_description} onChange={(e) => ctx.set("business_description", e.target.value)}
        placeholder="What does your business do, and who are your customers?" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Avg. monthly revenue (USD)" required error={ctx.errors.avg_monthly_revenue}>
        <TextInput data-testid="app-revenue" inputMode="numeric" invalid={!!ctx.errors.avg_monthly_revenue}
          value={ctx.form.avg_monthly_revenue}
          onChange={(e) => ctx.set("avg_monthly_revenue", e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
          placeholder="42000" />
      </Field>
      <Field label="Requested funding amount (USD)" required error={ctx.errors.requested_amount}>
        <TextInput data-testid="app-amount" inputMode="numeric" invalid={!!ctx.errors.requested_amount}
          value={ctx.form.requested_amount}
          onChange={(e) => ctx.set("requested_amount", e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
          placeholder="75000" />
      </Field>
    </div>
    <Field label="Use of funds (select all that apply)" error={ctx.errors.use_of_funds}>
      <ChipMulti testid="app-use" values={ctx.form.use_of_funds} onToggle={ctx.toggleUse}
        options={["Working capital", "Purchase Inventory", "Buy Equipment", "Expansion", "Payroll", "Marketing", "Refinance debt", "Real estate", "Other"].map((v) => ({ value: v, label: v }))} />
    </Field>
  </div>
);

export const OwnerSection = ({ ctx }) => (
  <div className="space-y-7">
    <Field label="Full name" required error={ctx.errors.owner_full_name}>
      <TextInput data-testid="app-owner-name" invalid={!!ctx.errors.owner_full_name}
        value={ctx.form.owner_full_name} onChange={(e) => ctx.set("owner_full_name", e.target.value)}
        placeholder="Ada Lovelace" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Email" required error={ctx.errors.owner_email}>
        <TextInput data-testid="app-owner-email" type="email" invalid={!!ctx.errors.owner_email}
          value={ctx.form.owner_email} onChange={(e) => ctx.set("owner_email", e.target.value)}
          placeholder="ada@acmerobotics.com" />
      </Field>
      <Field label="Mobile phone" required error={ctx.errors.owner_mobile}>
        <TextInput data-testid="app-owner-mobile" type="tel" invalid={!!ctx.errors.owner_mobile}
          value={ctx.form.owner_mobile} onChange={(e) => ctx.set("owner_mobile", e.target.value)}
          placeholder="(555) 000-1234" />
      </Field>
    </div>
    <Field label="% of ownership">
      <TextInput data-testid="app-owner-pct" inputMode="numeric"
        value={ctx.form.owner_ownership_pct}
        onChange={(e) => ctx.set("owner_ownership_pct", e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
        placeholder="100" />
    </Field>
    <Field label="Social Security Number" required error={ctx.errors.owner_ssn} hint="Encrypted in transit. Only authorized staff can view.">
      <TextInput data-testid="app-owner-ssn" invalid={!!ctx.errors.owner_ssn} inputMode="numeric"
        value={ctx.form.owner_ssn} onChange={(e) => ctx.set("owner_ssn", fmtSSN(e.target.value))}
        placeholder="123-45-6789" />
    </Field>
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Date of birth" required error={ctx.errors.owner_dob}>
        <TextInput data-testid="app-owner-dob" type="date" invalid={!!ctx.errors.owner_dob}
          value={ctx.form.owner_dob} onChange={(e) => ctx.set("owner_dob", e.target.value)} />
      </Field>
      <Field label="Estimated credit score">
        <SelectInput data-testid="app-owner-credit"
          value={ctx.form.owner_credit_score} onChange={(e) => ctx.set("owner_credit_score", e.target.value)}
          options={["Below 580", "580 – 619", "620 – 679", "680 – 739", "740+"]}
          placeholder="Select range" />
      </Field>
    </div>
    <Field label="Home address" required error={ctx.errors.owner_home_address}>
      <TextInput data-testid="app-owner-address" invalid={!!ctx.errors.owner_home_address}
        value={ctx.form.owner_home_address} onChange={(e) => ctx.set("owner_home_address", e.target.value)}
        placeholder="88 Analytical Ln, Austin TX 78701" />
    </Field>
  </div>
);

export const SecondOwnerSection = ({ ctx }) => (
  <div className="space-y-6">
    {!ctx.form.add_second_owner ? (
      <button
        type="button"
        data-testid="app-second-toggle"
        onClick={() => ctx.set("add_second_owner", true)}
        className="rounded-full border border-black/10 px-6 py-3 text-sm font-semibold text-[#007AFF] transition-all duration-300 hover:border-[#007AFF]/50 hover:bg-[#007AFF]/5"
      >
        + Add a second owner
      </button>
    ) : (
      <div className="space-y-6 rounded-2xl border border-black/8 bg-[#F6F9F8] p-6">
        <div className="flex items-center justify-between">
          <p className="font-display text-lg font-bold text-[#0A1628]">Second owner</p>
          <button type="button" data-testid="app-second-remove" onClick={() => ctx.set("add_second_owner", false)}
            className="rounded-full p-2 text-[#52525B] hover:bg-red-50 hover:text-red-500 transition-colors" aria-label="Remove second owner">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Field label="Full name" required error={ctx.errors.so_name}>
          <TextInput data-testid="app-so-name" invalid={!!ctx.errors.so_name} value={ctx.form.second_owner.name}
            onChange={(e) => ctx.setSO("name", e.target.value)} placeholder="Grace Hopper" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Email" required error={ctx.errors.so_email}>
            <TextInput data-testid="app-so-email" type="email" invalid={!!ctx.errors.so_email} value={ctx.form.second_owner.email}
              onChange={(e) => ctx.setSO("email", e.target.value)} placeholder="grace@acmerobotics.com" />
          </Field>
          <Field label="Mobile phone" required error={ctx.errors.so_mobile}>
            <TextInput data-testid="app-so-mobile" type="tel" invalid={!!ctx.errors.so_mobile} value={ctx.form.second_owner.mobile}
              onChange={(e) => ctx.setSO("mobile", e.target.value)} placeholder="(555) 000-5678" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="% of ownership">
            <TextInput data-testid="app-so-pct" inputMode="numeric" value={ctx.form.second_owner.pct}
              onChange={(e) => ctx.setSO("pct", e.target.value.replace(/[^\d]/g, "").slice(0, 3))} placeholder="35" />
          </Field>
          <Field label="Social Security Number" hint="Encrypted in transit.">
            <TextInput data-testid="app-so-ssn" inputMode="numeric" value={ctx.form.second_owner.ssn}
              onChange={(e) => ctx.setSO("ssn", fmtSSN(e.target.value))} placeholder="987-65-4321" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Date of birth">
            <TextInput data-testid="app-so-dob" type="date" value={ctx.form.second_owner.dob}
              onChange={(e) => ctx.setSO("dob", e.target.value)} />
          </Field>
          <Field label="Home address">
            <TextInput data-testid="app-so-address" value={ctx.form.second_owner.home_address || ""}
              onChange={(e) => ctx.setSO("home_address", e.target.value)} placeholder="12 Compiler Ct, Austin TX" />
          </Field>
        </div>
      </div>
    )}
  </div>
);

const RobotScan = () => (
  <svg viewBox="0 0 80 80" className="w-14" fill="none" aria-hidden="true">
    <line x1="40" y1="8" x2="40" y2="17" stroke="#0A1628" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="40" cy="6" r="4" fill="#14D8A5" />
    <rect x="20" y="17" width="40" height="26" rx="9" fill="#FFFFFF" stroke="#0A1628" strokeWidth="2.5" />
    <rect x="26" y="24" width="28" height="14" rx="7" fill="#0B1B33" />
    <circle cx="35" cy="31" r="3.2" fill="#4DA3FF" className="lr-eye" />
    <circle cx="45" cy="31" r="3.2" fill="#4DA3FF" className="lr-eye" />
    <rect x="25" y="47" width="30" height="22" rx="8" fill="#FFFFFF" stroke="#0A1628" strokeWidth="2.5" />
    <path d="M32 55h16M32 61h10" stroke="#007AFF" strokeWidth="2" strokeLinecap="round" className="animate-pulse" />
  </svg>
);

export const DocumentsSection = ({ ctx }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [scanning, setScanning] = useState(false);

  const add = (list) => {
    const ok = [];
    const problems = [];
    for (const f of list) {
      if (!/\.(pdf|jpe?g|png|heic|heif)$/i.test(f.name)) problems.push(`${f.name}: unsupported type`);
      else if (f.size > 15 * 1024 * 1024) problems.push(`${f.name}: over 15MB`);
      else ok.push(f);
    }
    if (ok.length) {
      setScanning(true);
      setTimeout(() => setScanning(false), 1400);
    }
    ctx.setFiles([...ctx.files, ...ok].slice(0, 4));
    ctx.setZoneErr(list.length && !ok.length ? problems.join(" · ") : problems.join(" · "));
  };

  return (
    <div className="space-y-5">
      <div
        data-testid="upload-zone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add([...e.dataTransfer.files]); }}
        className={`cursor-pointer rounded-2xl border-2 border-dashed px-8 py-14 text-center transition-all duration-300 ${
          drag ? "border-[#007AFF] bg-[#007AFF]/5" : "border-black/15 bg-[#F6F9F8] hover:border-[#007AFF]/50"
        }`}
      >
        <input
          data-testid="upload-input"
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.heic"
          className="hidden"
          onChange={(e) => { add([...e.target.files]); e.target.value = ""; }}
        />
        {scanning ? (
          <div data-testid="upload-scanning" className="flex flex-col items-center gap-3">
            <span className="lr-float"><RobotScan /></span>
            <p className="label-mono text-[#007AFF] animate-pulse">Robo-B7 is scanning your documents…</p>
          </div>
        ) : (
          <>
            <UploadCloud className={`w-10 h-10 mx-auto ${drag ? "text-[#007AFF]" : "text-[#52525B]"}`} />
            <p className="mt-4 font-display text-lg font-semibold text-[#0A1628]">
              Drop your last 4 months of business bank statements here
            </p>
            <p className="label-mono mt-2 text-[#52525B]">
              PDF, JPG, PNG, HEIC · up to 15MB per file · click or drag &amp; drop
            </p>
            <p className="label-mono mt-1 text-[#007AFF]">{ctx.files.length}/4 added</p>
          </>
        )}
      </div>

      {ctx.zoneErr && <p data-testid="upload-error" className="text-xs font-medium text-red-500">{ctx.zoneErr}</p>}
      {ctx.errors.files && <p className="text-xs font-medium text-red-500">{ctx.errors.files}</p>}

      {ctx.files.map((f, i) => (
        <div key={i} data-testid={`upload-file-${i}`} className="flex items-center justify-between rounded-xl border border-black/8 bg-white px-4 py-3">
          <span className="flex items-center gap-3 min-w-0">
            <FileIcon className="w-4 h-4 text-[#007AFF] shrink-0" />
            <span className="truncate text-sm text-[#0A1628]">{f.name}</span>
          </span>
          <span className="flex items-center gap-3 shrink-0 ml-4">
            <span className="mono text-xs text-[#52525B]">{(f.size / 1024 / 1024).toFixed(2)}MB</span>
            <button type="button" data-testid={`remove-file-${i}`} onClick={() => ctx.setFiles(ctx.files.filter((_, j) => j !== i))}
              className="rounded-full p-1.5 text-[#52525B] hover:bg-red-50 hover:text-red-500 transition-colors" aria-label="Remove file">
              <X className="w-4 h-4" />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
};

export const SignSection = ({ ctx }) => (
  <div className="space-y-7">
    <div className="lr-scroll rounded-2xl border border-black/8 bg-[#F6F9F8] p-6 max-h-72 overflow-y-auto" data-testid="app-authorization" data-lenis-prevent>
      <p className="text-xs leading-relaxed text-[#52525B]">
        By signing this application, I affirm the accuracy and truthfulness of all provided
        information. I grant LendRobot and its representatives permission to obtain consumer
        and/or personal, business, and investigative reports for commercial loans, including
        daily repayment features or future receivables purchases. This authorization extends to
        accessing information from TransUnion, Experian, Equifax, other credit bureaus, banks,
        creditors, and third parties. I also authorize the sharing of this application and
        related information with the involved parties.
      </p>
      <p className="mt-3 text-xs leading-relaxed text-[#52525B]">
        I consent to the release of information by creditors or financial institutions to
        LendRobot and its representatives. I provide my business cell phone number and email
        address, consenting to receive correspondence and messages regarding transactions. I
        understand I may receive text messages, with an anticipated frequency of approximately
        10 per month, and acknowledge that consent to receive texts is not a condition of
        approval. I certify the completeness, truth, and accuracy of all provided information.
      </p>
    </div>
    <label className="flex items-start gap-3 text-sm text-[#0A1628] cursor-pointer">
      <input data-testid="app-agree" type="checkbox" checked={ctx.form.agree} onChange={(e) => ctx.set("agree", e.target.checked)}
        className="mt-0.5 accent-[#007AFF]" style={{ width: 18, height: 18 }} />
      <span>I have read and agree to the authorization above. <span className="text-red-500">*</span></span>
    </label>
    {ctx.errors.agree && <p className="text-xs font-medium text-red-500">{ctx.errors.agree}</p>}
    <div className="grid sm:grid-cols-2 gap-6">
      <Field label="Signature (type your full name)" required error={ctx.errors.signature}>
        <TextInput data-testid="app-signature" invalid={!!ctx.errors.signature}
          value={ctx.form.signature} onChange={(e) => ctx.set("signature", e.target.value)}
          placeholder="Ada Lovelace" />
      </Field>
      <Field label="Date">
        <div data-testid="app-date" className="rounded-xl border border-black/10 bg-[#F6F9F8] px-4 py-3.5 text-sm text-[#52525B] mono">
          {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </Field>
    </div>
    <p className="rounded-xl bg-[#14D8A5]/10 border border-[#14D8A5]/30 px-4 py-3 text-xs text-[#0A1628]">
      Submitting does not impact your personal credit. A team member contacts you before any hard pull.
    </p>
  </div>
);
